import { useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../../supabase/browser.ts";

export interface BoardRoundsCard {
  id: string;
  match_id: string;
  fixture_number: number;
  fixture_label: string;
  group_code: string | null;
  venue: string | null;
  kickoff_utc: string;
  kickoff_wat: string;
  strikza_call: string | null;
  confidence_tier: "A" | "B" | "C" | null;
  primary_market: string | null;
  secondary_market: string | null;
  ifpe_fair_price: number | null;
  bookmaker_price: number | null;
  ev_delta: number | null;
  clv_gate: "PASS" | "CONDITIONAL_PASS" | "FAIL" | null;
  clv_gate_reason: string | null;
  squad_confidence: "FULL" | "ONE_KEY_ABSENCE" | "DISRUPTED" | null;
  intelligence_note: string | null;
  narrative_flag: string | null;
  upset_flag: string | null;
  slip_inclusion: string[];
  line_moved: boolean;
  squad_census: Record<string, unknown> | null;
  national_form_tfs: number | null;
  h2h_dominance: string | null;
  env_advantage_score: number | null;
  env_flags: Record<string, unknown> | null;
  motivation_pressure: string | null;
  referee_profile: string | null;
  dna_composite: number | null;
  dna_flags: Record<string, unknown> | null;
  opening_line: Record<string, unknown> | null;
  ifpe_home_win: number | null;
  ifpe_draw: number | null;
  ifpe_away_win: number | null;
  ifpe_btts_yes: number | null;
  ifpe_over_25: number | null;
  ifpe_under_25: number | null;
  ifpe_ahc: Record<string, unknown> | null;
  ifpe_confidence: number | null;
  ifpe_all_markets: Record<string, number> | null;
  secondary_ev_scores: Record<string, unknown> | null;
  secondary_picks: Array<{
    code: string; label: string; prob: number;
    fair_odds: number; book_odds: number | null;
    ev: number; conviction: string; reasoning: string;
  }> | null;
  upset_probability: string | null;
  narrative_distortion: Record<string, unknown> | null;
  booking_code: string | null;
}

export interface AgentStatus {
  agent_id: string;
  layer: number;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED" | "SKIPPED";
  completed_at: string | null;
  duration_ms: number | null;
}

export interface LineCheck {
  check_type: string;
  checked_at: string;
  market: string;
  previous_odds: number;
  current_odds: number;
  movement: number;
  triggered_recheck: boolean;
}

export interface ClvAuditEntry {
  market: string;
  selection: string;
  strikza_fair: number;
  opening_line: number;
  closing_line: number | null;
  clv_delta: number | null;
  beat_closing: boolean | null;
  prediction_hit: boolean | null;
}

export interface BoardRoundsCardData {
  card: BoardRoundsCard | null;
  agents: AgentStatus[];
  line_checks: LineCheck[];
  clv_audit: ClvAuditEntry[];
}

export function useBoardRoundsCard(matchId: string | null): {
  data: BoardRoundsCardData | null;
  loading: boolean;
  error: string | null;
} {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [data, setData] = useState<BoardRoundsCardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!matchId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (supabase.rpc as unknown as (name: string, params: Record<string, unknown>) => Promise<{
      data: unknown;
      error: { message: string } | null;
    }>)("get_board_rounds_card", { p_match_id: matchId })
      .then(({ data: d, error: e }) => {
        if (cancelled) return;
        if (e) { setError(e.message); setData(null); }
        else { setData((d ?? null) as BoardRoundsCardData | null); }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [supabase, matchId]);

  return { data, loading, error };
}
